import { LoginResponse, Menus, MenuType } from './dto/response';
import { DownloadFiles } from './services/project/project';
import { showSnackbar } from '../redux/reducer/snackbarSlice';
import * as XLSX from "xlsx-js-style";
import * as FileSaver from "file-saver";
import { ENC_KEY_STRING, IV_STRING, REACT_APP_CDN_URL } from './base-urls';
import CryptoJS from 'crypto-js';

const getEncryptionConfig = () => {
    if (!ENC_KEY_STRING || !IV_STRING) {
        throw new Error('Encryption key or IV is not defined in environment variables.');
    }
    const key = CryptoJS.enc.Utf8.parse(ENC_KEY_STRING);
    const iv = CryptoJS.enc.Utf8.parse(IV_STRING);
    return { keySize: 128, iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7, key };
};

export function encryptText(plainText: any) {
    if (!plainText) return ''
    const config = getEncryptionConfig();
    let encPassword = CryptoJS.AES.encrypt(plainText, config.key, config);
    return encPassword.ciphertext.toString(CryptoJS.enc.Base64);
}

export const decryptText = (text: unknown): string => {
    if (typeof text !== 'string' || !text.trim()) return '';
    const config = getEncryptionConfig();
    try {
        const decrypted = CryptoJS.AES.decrypt(text, config.key, config);
        const result = decrypted.toString(CryptoJS.enc.Utf8);
        return result ?? text;
    } catch {
        try {
            const parsed = JSON?.parse(text);
            return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
        } catch {
            return text;
        }
    }
};


export const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null

export const stableSort = <T>(array: readonly T[], comparator: (a: T, b: T) => number) => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export const getComparator = <Key extends keyof any>(order: any, orderBy: Key,): (
    a: { [key in Key]: any },
    b: { [key in Key]: any },
) => number => {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

export const isLoggedIn = (state: LoginResponse) => {
    const { token, userDetails, session_expires } = state;
    return !!userDetails && !!token && !!session_expires;
}


export const isAuthorizedPage = (moduleUrls: string) => {
    return true
}


export const rupeeAmountDisplay = (amount: number) => {
    if (!amount) {
        return `₹ 0`
    }
    return `₹ ${AmountDisplay(amount)}`;
};

export const AmountDisplay = (amount: number) => {
    if (!amount) {
        return '0';
    }
    const formattedAmount = new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    }).format(amount);

    return `${formattedAmount}`;
};

export const hasAccessToPath = (menus: any, path: string): MenuType | null => {
    if (!menus) return null;

    for (const menu of menus) {
        if (menu.url === path) {
            return menu;
        }

        if (menu.sub_menus && menu.sub_menus.length > 0 && hasAccessToPath(menu.sub_menus, path)) {
            return menu.sub_menus.find((menu: any) => menu.url === path) || null;
        }
    }

    return null;
};

export const formatDuration = (m: number) => {
    return `${m >= 12 ? `${Math.floor(m / 12)} Year${m % 12 ? ' ' : ''}` : ''}${m % 12 ? `${m % 12} Month` : m < 12 ? `${m} Month` : ''}` || '0 Month';
}

export const Capitalize = (text?: string): string => {
    if (!text) return '--';
    return text
        .replace(/[_-]/g, ' ') // replace underscores and hyphens with spaces
        .toLowerCase() // convert the whole string to lowercase
        .split(' ') // split into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each word
        .join(' '); // join them back with space
};


export const formatUserType = (userType?: string) => {
    if (!userType) return "";
    return userType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const handleDownload = async (
    path: string,
    name: string,
    dispatch: any
) => {
    const payload = { path };
    const { code, data, message } = await DownloadFiles(payload);
    if (code === 200) {
        const extension = path?.split('.').pop()?.toLowerCase() || 'bin';
        const mimeMap: { [key: string]: string } = {
            pdf: 'application/pdf',
            png: 'image/png',
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            webp: 'image/webp',
            gif: 'image/gif',
            svg: 'image/svg+xml',
        };

        const mimeType = mimeMap[extension] || 'application/octet-stream';

        const blob = new Blob([data], { type: mimeType });
        const url = URL.createObjectURL(blob);

        const fileName = name.includes('.') ? name : `${name}.${extension}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } else {
        dispatch(
            showSnackbar({
                type: 'error',
                message: message || 'Something went wrong during file download',
            })
        );
    }
};

export const handleConvertToBlob = async (path: string, dispatch: any) => {
    const payload = { path };
    const { code, data } = await DownloadFiles(payload);

    if (code === 200) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        return url;
    } else {
        dispatch(showSnackbar({ type: "error", message: 'Something Went Wrong' }));
    }
    return "";
};

export const handleConvertToBlobimage = async (path: string, dispatch: any) => {
    const payload = { path };
    const { code, data, message } = await DownloadFiles(payload);

    if (code === 200) {
        const blob = new Blob([data], { type: 'image/jpeg' });
        const url = window.URL.createObjectURL(blob);
        return url;
    } else {
        dispatch(showSnackbar({ type: "error", message: 'Something Went Wrong' }));
    }
    return "";
};

export const extractFilenameFromFilePath = (filePath: string | null | undefined): string => {
    if (!filePath) return "";
    const parts = filePath.split("/");
    return parts[parts.length - 1] || "";
};

export const getFileDetails = (filePath: string) => {
    const fullName = filePath?.split('/')?.pop() || filePath; // gets last part after "/"
    const parts = fullName?.split('.');
    const extension = parts?.length > 1 ? parts?.pop()!.toLowerCase() : '';
    const name = parts?.join('.');
    return { name, extension };
};

export const numberToIndianWords = (num: number) => {
    const ones = [
        "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
        "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
        "Seventeen", "Eighteen", "Nineteen"
    ];

    const tens = [
        "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
    ];

    const units = [
        "", "Thousand", "Lakh", "Crore", "Arab", "Kharab", "Neel", "Padma", "Shankh", "Mahashankh"
    ];

    const convert = (num: number) => {
        if (num === 0) return "";
        let result = "";

        if (num >= 100) {
            result += ones[Math.floor(num / 100)] + " Hundred ";
            num %= 100;
        }

        if (num >= 20) {
            result += tens[Math.floor(num / 10)] + " ";
            num %= 10;
        }

        if (num > 0) {
            result += ones[num] + " ";
        }

        return result.trim();
    };

    if (num === 0) return "Zero Rupees";
    if (num > 999999999999999999) {
        return "Number exceeds 18 digits";
    }

    let result = "";
    let unitIndex = 0;
    if (num % 1000 > 0) {
        result = convert(num % 1000) + " " + units[unitIndex] + " ";
    }
    num = Math.floor(num / 1000);
    unitIndex++;
    while (num > 0 && unitIndex < units.length) {
        if (num % 100 > 0) {
            result = convert(num % 100) + " " + units[unitIndex] + " " + result;
        }
        num = Math.floor(num / 100);
        unitIndex++;
    }

    return result.trim() + " Rupees";
};

export function exportToExcel(data: any[], fileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    const workbook: XLSX.WorkBook = {
        Sheets: { Sheet1: worksheet },
        SheetNames: ["Sheet1"]
    };

    const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array"
    });

    const blob = new Blob([excelBuffer], {
        type: "application/octet-stream"
    });

    FileSaver.saveAs(blob, `${fileName}.xlsx`);
}
export const handleFilePreview = (
    file: File | Blob | string | null,
    title: string,
    options: {
        setPreviewFile: (url: string) => void;
        setPreviewTitle: (title: string) => void;
        setOpenPreview: (open: boolean) => void;
    },
    dispatch: any
) => {

    const { setPreviewFile, setPreviewTitle, setOpenPreview } = options;


    if (!file) {
        return;
    }

    setPreviewTitle(title);

    if (file instanceof File || file instanceof Blob) {
        const fileUrl = URL.createObjectURL(file);
        if (file.type === "application/pdf") {
            window.open(fileUrl, "_blank");
        } else {
            setPreviewFile(fileUrl);
            setOpenPreview(true);
        }
        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } else if (typeof file === "string") {
        const fullUrl = REACT_APP_CDN_URL + file;
        window.open(fullUrl, "_blank");
    } else {
        console.error("Unsupported file type:", file);
    }
};
export function filterNavigableMenus(
    menuData: (Menus | null | undefined)[]
): Menus[] {
    if (!Array.isArray(menuData)) return [];

    const result = menuData.reduce<Menus[]>((acc, item) => {
        if (
            !item ||
            item.is_navigation !== 1 ||
            !Array.isArray(item.permissions) ||
            item.permissions.length === 0
        ) {
            return acc;
        }

        let validSubMenus: Menus[] | null = null;

        if (Array.isArray(item.sub_menus)) {
            for (const sub of item.sub_menus) {
                if (
                    sub &&
                    sub.is_navigation === 1 &&
                    Array.isArray(sub.permissions) &&
                    sub.permissions.length > 0
                ) {
                    validSubMenus ||= [];
                    validSubMenus.push(sub);
                }
            }

            // 🔹 sort sub-menus by menu_order
            if (validSubMenus) {
                validSubMenus.sort(
                    (a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0)
                );
            }
        }

        acc.push(
            validSubMenus
                ? { ...item, sub_menus: validSubMenus }
                : { ...item, sub_menus: null }
        );

        return acc;
    }, []);

    // 🔹 sort main menus by menu_order
    return result.sort(
        (a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0)
    );
}


export async function aesEncrypt(plainText: string, secretKey: string): Promise<string> {
    const enc = new TextEncoder();

    // 1️⃣ Derive 256-bit key from secret
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        enc.encode(secretKey),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    const salt = enc.encode("static-salt"); // MUST be same in Go
    const key = await crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt,
            iterations: 100000,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt"]
    );

    // 2️⃣ Generate IV (nonce)
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // 3️⃣ Encrypt
    const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        enc.encode(plainText)
    );

    // 4️⃣ Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // 5️⃣ Base64 encode
    return btoa(String.fromCharCode(...combined));
}